import React from 'react';
import { BsThreeDotsVertical, BsDownload, BsPrinter } from 'react-icons/bs';
import { FaEye } from 'react-icons/fa6';
import { MdOutlineDeleteForever } from 'react-icons/md';
import Pagination from '../Pagination';
import { useNavigate } from 'react-router-dom';
import useUserManagement from '../../hooks/UserManagement/useUserManagement';
import Title from '../Title';
import { BeatLoader } from 'react-spinners';
import { FcGlobe } from 'react-icons/fc';

const UserList = () => {
  const navigate = useNavigate();
  const {
    activePopoverId,
    paginatedPeople,
    totalEntries,
    deleteUser,
    exportToCSV,
    handlePrint,
    togglePopover,
    isLoading,
    getCountryAbbreviation,
  } = useUserManagement();

  const handleUserClick = userId => {
    navigate(`${userId}`);
  };

  const getBusinessCount = businesses => {
    if (Array.isArray(businesses)) {
      return new Set(businesses).size;
    }
    return 0;
  };

  if (isLoading) {
    return (
      <div role="status" className="p-4 h-screen bg-[#F4F5F6] flex justify-center items-center">
        <BeatLoader color="#000000" size={15} />
      </div>
    );
  }

  return (
    <div className="pb-4 bg-[#F4F5F6]">
      {/* Header Section */}
      <div className="my-2">
        <Title title="User Management" subtitle="Home &gt; User List" />
      </div>
      <div className="mt-8">
        <div className="bg-white rounded-lg max-h-[680px] overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-2">
            <h2 className="text-base font-medium text-[#191B1C]">User List</h2>
            <div className="flex items-end justify-end gap-2">
              <button
                onClick={exportToCSV}
                className="flex items-center gap-1 p-2 bg-gray-100 rounded-md hover:bg-gray-200">
                <BsDownload className="text-gray-600" />
                <span className="text-sm font-medium">Export CSV</span>
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-1 p-2 bg-gray-100 rounded-md hover:bg-gray-200">
                <BsPrinter className="text-gray-600" />
                <span className="text-sm font-medium">Print</span>
              </button>
            </div>
          </div>
          <div className="relative overflow-x-auto">
            <table id="user-table" className="w-full mx-auto text-left">
              <thead>
                <tr className="font-nohemi text-sm text-[#808587] bg-[#F5F6F7]">
                  <th className="p-3 font-light" />
                  <th className="p-3 font-light">User ID</th>
                  <th className="p-3 font-light">Title/Designation</th>
                  <th className="p-3 font-light">Name</th>
                  <th className="p-3 font-light">Phone</th>
                  <th className="p-3 font-light">Email</th>
                  <th className="p-3 font-light">Status</th>
                  <th className="p-3 font-light">No of Business</th>
                  <th className="p-3 font-light">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPeople.map((user, index) => {
                  return (
                    <tr
                      key={user._id}
                      onClick={() => handleUserClick(user._id)}
                      className="border-b font-nohemi font-light text-sm text-[#4A5154] hover:bg-[#F0F6FF] cursor-pointer">
                      <td className="py-2 pl-4">
                        <input
                          type="checkbox"
                          className="form-checkbox accent-gray-700 bg-[#E5E7E8]"
                        />
                      </td>

                      <td className="py-2 pl-3">{user.id || '-'}</td>
                      <td className="px-4 py-2 font-semibold text-[#191B1C]">
                        {user.title || '-'}
                      </td>

                      <td className="flex items-center px-3 pt-4 pb-2 space-x-2">
                        {user.country ? (
                          <img
                            src={`https://flagsapi.com/${getCountryAbbreviation(
                              user.country
                            )}/flat/64.png`}
                            alt={user.country}
                            className="w-4 h-4"
                          />
                        ) : (
                          <FcGlobe className="w-4 h-4 " />
                        )}

                        <span>
                          {user.firstName} {user.lastName}
                        </span>
                      </td>

                      <td className="px-2 py-2">{user.phoneNumber || '-'}</td>
                      <td className="px-3 py-2">{user.email || '-'}</td>

                      <td className="px-3 py-2">
                        <span className={`${user.isActive ? 'text-green-500' : 'text-orange-500'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      <td className="p-2 text-sm text-center">
                        {getBusinessCount(user.business) || '-'}
                      </td>

                      <td className="p-2">
                        <div className="relative inline-block">
                          <button
                            className="focus:ring-0 focus:outline-none mx-2 w-[80%] h-[50%] rounded-full"
                            onClick={e => {
                              e.stopPropagation();
                              togglePopover(user._id);
                            }}>
                            <BsThreeDotsVertical className="text-[#191B1C]" />
                          </button>

                          {activePopoverId === user._id && (
                            <div
                              role="tooltip"
                              className="absolute z-10 mt-1 text-sm text-gray-500 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-md top-full left-1/2 w-max"
                              onClick={e => e.stopPropagation()}>
                              <div className="flex flex-col p-2 space-y-1">
                                <button
                                  className="flex items-center text-[10px] text-gray-700 hover:bg-gray-100 px-2 py-1 rounded-md"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleUserClick(user._id);
                                  }}>
                                  <FaEye className="mr-1" />
                                  View
                                </button>

                                <button
                                  className="flex items-center text-[10px] text-gray-700 hover:bg-gray-100 px-2 py-1 rounded-md"
                                  onClick={e => {
                                    e.stopPropagation();
                                    deleteUser(user._id);
                                  }}>
                                  <MdOutlineDeleteForever size={18} className="mr-1 text-red-500" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination totalEntries={totalEntries} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserList;
